import React, { Fragment } from "react";
import { StepRenderProps } from "../../../interfaces/FlowInterfaces";
import { createStyles, makeStyles, TableCell, Theme, Typography } from "@material-ui/core";
import { observer } from "mobx-react";
import { Alert, AlertTitle } from "@material-ui/lab";
import { OffsetSelectionStepValues, VolunteerRegistrationFlowExternalState } from "../../_interfaces/IAdminVolunteerRegistrationStepTypeValues";
import { IAdminVolunteerRegistrationFlowValues } from "../../_interfaces/IAdminVolunteerRegistrationFlowValues";
import RecordTable from "../../../../../components/Shared/Table/NewRecordTable";
import { ISortableTableHeader, TableHeader } from "../../../../../stores/models/TableHeader";
import { Option } from "../../../../../stores/models/Option";
import { useOffsetSelectionController } from "../../hooks/useOffsetSelectionController";
import { IShift, Shift } from "../../../../../stores/models/Shift";
import FormLoadingSpinner from "../../../../../components/Organization/VolunteerOpportunities/FormLoadingSpinner";
import { DialogState } from "../../../../../stores/models/DialogState";
import { StylizedInternalLink } from "../../../../../components/Navigation/Components";
import clsx from "clsx";
import { useStandardStyles } from "../../../../../shared/general/hooks/useStandardStyles";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        header: {
            marginBottom: theme.spacing(2),
        },
        section: {
            marginBottom: theme.spacing(2),
        },
        alert: {
            marginBottom: theme.spacing(2),
            boxSizing: 'border-box',
            textAlign: 'left',
            flexGrow: 1,
            whiteSpace: 'pre-wrap'
        },
        avatar: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            width: '75px',
            height: '75px',
            margin: '16px auto',
            marginTop: theme.spacing(4),
        },
        prompt: {
            marginTop: theme.spacing(2),
            color: theme.palette.action.active
        },
        filterToolbar: {
            marginTop: theme.spacing(0),
            [theme.breakpoints.down('sm')]: {
                marginBottom: theme.spacing(1)
            }
        },
        selectionToolbar: {
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(-1)
            },
            marginTop: theme.spacing(-1.5)
        },
        noWrap: {
            display: 'flex',
            flexWrap: 'nowrap',
            margin: '-6px 0px'
        },
        chip: {
            '& .MuiChip-icon, .MuiChip-label': {
                color: theme.palette.darkBackground.main
            },
            '&:not(:last-child)': {
                marginRight: theme.spacing(.5),
            },
            marginBottom: theme.spacing(.5),
        },
        tableChip: {
            backgroundColor: theme.palette.primaryBackground.main,
        },
        link: {
            color: theme.palette.primary.main
        },
        viewButton: {
            marginLeft: theme.spacing(1)
        },
    })
);

const headCells = ([
    { id: 'checkbox', alignment: 'center', disablePadding: true, sortable: false, checkbox: true },
    { id: 'date', alignment: 'left', disablePadding: false, label: 'Date', sortable: true, sortValue: (shift: IShift) => new Shift(shift).repetitionPattern.startTimestamp, direction: 'asc' },
    { id: 'time', alignment: 'left', disablePadding: false, label: 'Time', sortable: false },
    {
        id: 'status', alignment: 'left', disablePadding: false, label: 'Slots Filled', sortable: true, sortValue: (shiftData: IShift) => {
            const shift = new Shift(shiftData);
            return shift.slots - shift.slotsLeft;
        }
    },
] as ISortableTableHeader<IShift>[]).map(headCell => new TableHeader(headCell));

export const OffsetSelectionStep: React.FC<
    StepRenderProps<OffsetSelectionStepValues, IAdminVolunteerRegistrationFlowValues, VolunteerRegistrationFlowExternalState>
> = observer(({
    values,
    update,
    submit,
    canSubmit,
    validation,
    allValues,
    externallyManagedState
}) => {

    /********* React hooks *********/

    const classes = useStyles();
    const baseClasses = useStandardStyles();
    const controller = useOffsetSelectionController(
        values,
        allValues,
        externallyManagedState
            ? externallyManagedState
            : { registeredVolunteerIds: [], topLevelShift: new Shift(), dialogState: new DialogState() },
        update,
        submit
    );

    const tableController = controller.values.offsetsTableController;

    /********* Render *********/

    return (
        <Fragment>
            {/* Error Notification */}
            {controller.fieldErrors && Object.values(controller.fieldErrors).length > 0
                ? <Alert severity="error" className={classes.alert}>
                    <AlertTitle>Registration Error</AlertTitle>
                    {controller.fieldErrors.offsets}
                </Alert>
                : null
            }
            {controller.error
                ? <Alert severity="error" className={classes.alert}>
                    <AlertTitle>Registration Error</AlertTitle>
                    {controller.error}
                </Alert>
                : null
            }
            {/* Header */}
            <div>
                <Typography variant="h5" className={classes.header}>Shifts</Typography>
            </div>
            {/* Selected Volunteers Count */}
            {allValues.volunteersTableController?.persistentSelections
                ? <div className={classes.section}>
                    <Typography className={baseClasses.bold}>Selected Volunteers</Typography>
                    <div className={baseClasses.flex}>
                        <Typography>
                            {
                                allValues.volunteersTableController?.persistentSelections.options.length === 1
                                    ? `${allValues.volunteersTableController?.persistentSelections.options.length} volunteer selected`
                                    : `${allValues.volunteersTableController?.persistentSelections.options.length} volunteers selected`
                            }
                        </Typography>
                        <StylizedInternalLink
                            onClick={() => {
                                update({ viewVolunteers: true, backClicked: true });
                                submit({ viewVolunteers: true, backClicked: true });
                            }}
                            children={<Typography className={clsx(classes.link, classes.viewButton)}>View Selected</Typography>}
                        />
                    </div>
                </div>
                : null
            }
            {/* Shift Instances Table */}
            {tableController
                ? <RecordTable
                    controller={tableController}
                    tableHeaderCells={headCells}
                    displayFunctions={[
                        (option: Option<IShift>, rowIndex: number, cellIndex: number) => {
                            return null;
                        },
                        (option: Option<IShift>, rowIndex: number, cellIndex: number) => {
                            return (
                                <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                    {new Shift(option.object).repetitionPattern.formattedStartDate}
                                </TableCell>
                            );
                        },
                        (option: Option<IShift>, rowIndex: number, cellIndex: number) => {
                            return (
                                <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                    {new Shift(option.object).repetitionPattern.formattedTime}
                                </TableCell>
                            );
                        },
                        (option: Option<IShift>, rowIndex: number, cellIndex: number) => {
                            const shift = new Shift(option.object);
                            const slotsFilled = shift.numRegistrations;
                            const unlimitedSlots = shift.unlimitedSlots;
                            return (
                                <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                    {`${slotsFilled}/${unlimitedSlots ? 'Unlimited' : option.object.slots}`}
                                </TableCell>
                            );
                        },
                    ]}
                    onRowClicked={(option: Option<IShift>, rowIndex: number) => {
                        option.toggleSelection();
                    }}
                />
                : null
            }
            {/* <RecordTable
                        controller={tableController}
                            // records={new OptionCollection('id', allValues.shiftInstances)}
                            tableHeaderCells={headCells}
                            orderBy={'date'}
                            order={'asc'}
                            // TODO: Figure out a plan for handling ongoing shifts for expired opportunities.
                            displayFunctions={[
                                // (option: Option<Shift>, rowIndex: number, cellIndex: number) => {
                                //     return (
                                //         <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                //             {props.getPositionForShift(option.object)}
                                //         </TableCell>
                                //     );
                                // },
                                (option: Option<Shift>, rowIndex: number, cellIndex: number) => {
                                    return null;
                                },
                                (option: Option<Shift>, rowIndex: number, cellIndex: number) => {
                                    return (
                                        <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                            {option.object.repetitionPattern.formattedStartDate}
                                        </TableCell>
                                    );
                                },
                                (option: Option<Shift>, rowIndex: number, cellIndex: number) => {
                                    return (
                                        <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                            {option.object.repetitionPattern.formattedTime}
                                        </TableCell>
                                    );
                                },
                                (option: Option<Shift>, rowIndex: number, cellIndex: number) => {
                                    const slotsFilled = option.object.numRegistrations;
                                    const unlimitedSlots = option.object.unlimitedSlots;
                                    return (
                                        <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                            {`${slotsFilled}/${unlimitedSlots ? 'Unlimited' : option.object.slots}`}
                                        </TableCell>
                                    );
                                },
                            ]}
                            onRowClicked={(option: Option<Shift>, rowIndex: number) => {
                                const shift = option.object;
                                option.toggleSelection();
                                // onShiftRowClicked(shift)
                            }}
                        /> */}
        </Fragment>
    );
});